import axiosInstance from "./api";
import API_URL from "@/globals/api";
import authHeader from "./auth-header";

/**
 * The /tutorial page's "Ask" box. The OpenRouter key and the tutorial text the
 * answers come from live on the backend (backend-nextja/src/server/tutorialAsk.ts);
 * only the question, the earlier turns and the reader's language go from here.
 *
 * POST /tutorial/ask  { question, history?, lang?, stream? }
 *
 *   history  previous turns, oldest first: [{ role: 'user'|'assistant', content }]
 *   lang     the panel's Google-Translate language, so the answer matches it
 *
 *   JSON:   { id, answer, steps }
 *   stream: text/event-stream —
 *             meta  { steps }               ranked steps, before the model runs
 *             delta { text }                answer text as it is written
 *             done  { id, answer, steps }   final answer and the steps it cites
 *             error { message }
 *
 *   `steps` are tutorial step ids (`order-2`), best first — the anchors of the
 *   screenshots to show. `id` identifies the logged question for feedback;
 *   null when the backend could not log it.
 *
 *   422 — empty or over-long question; 429 — 30 questions an hour used up;
 *   503 — the assistant is unavailable. All carry a `message`.
 *
 * POST /tutorial/feedback  { id, helpful }  →  { ok }
 * GET  /tutorial/questions?limit&before     admin only; 403 for everyone else.
 */
export const askTutorial = (payload) =>
  axiosInstance.post("/tutorial/ask", payload).then((response) => response.data);

export const sendTutorialFeedback = (id, helpful) =>
  axiosInstance.post("/tutorial/feedback", { id, helpful }).then((response) => response.data);

export const listTutorialQuestions = (params = {}) =>
  axiosInstance.get("/tutorial/questions", { params }).then((response) => response.data);

/** An error carrying the backend's status and message, like an axios error does. */
class AskError extends Error {
  constructor(status, message) {
    super(message);
    this.response = { status, data: { message } };
  }
}

/**
 * Streams an answer. Calls `on.meta(steps)`, `on.delta(text)` as text arrives,
 * and resolves with the `done` payload `{ id, answer, steps }`.
 *
 * Uses fetch, because axios cannot read a response body as it arrives in the
 * browser. That also means it skips the axios token-refresh interceptor, so a
 * 401 — or a browser or proxy that will not stream — falls back to the plain
 * JSON request, which refreshes and retries like every other call.
 */
export async function streamTutorialAnswer(payload, on = {}) {
  let response;
  try {
    response = await fetch(`${API_URL}/tutorial/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...authHeader() },
      body: JSON.stringify({ ...payload, stream: true }),
    });
  } catch {
    return fallback(payload, on);
  }

  const type = response.headers.get("content-type") || "";
  if (response.status === 401 || (response.ok && !type.includes("text/event-stream")) || !response.body) {
    return fallback(payload, on);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new AskError(response.status, body.message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = null;
  for (;;) {
    const { value, done: finished } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    // Events are separated by a blank line.
    let cut;
    while ((cut = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      let parsed;
      try {
        parsed = JSON.parse(data);
      } catch {
        continue;
      }
      if (event === "meta") on.meta?.(parsed.steps || []);
      else if (event === "delta") on.delta?.(parsed.text || "");
      else if (event === "done") done = parsed;
      else if (event === "error") throw new AskError(503, parsed.message);
    }
    if (finished) break;
  }
  if (!done) throw new AskError(503, "The answer was cut off. Please ask again.");
  return done;
}

async function fallback(payload, on) {
  const result = await askTutorial(payload);
  on.delta?.(result.answer || "");
  return result;
}
